import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  IconButton,
  Checkbox,
} from '@mui/material';

import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import Iconify from '../components/Iconify';
import { HrconclaveListToolbar, HrconclaveListHead, HrconclaveMoreMenu } from '../sections/reports';

const TABLE_HEAD = [
  { id: 'title', label: 'Main Title', alignRight: false },
  { id: 'subTitle', label: 'Sub Title', alignRight: false },
  { id: 'section1', label: 'Consulting Process', alignRight: false },
  { id: 'section2', label: 'Educational Offerings', alignRight: false },
  { id: 'section3', label: 'Additional Services', alignRight: false },
  { id: 'services', label: 'Services List', alignRight: false },
  { id: 'action', label: 'Action', alignRight: true },
];

export default function Hrconclave() {
  const [servicesData, setServicesData] = useState([]);
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('title');

  const [openModal, setOpenModal] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  // form state
  const [title, setTitle] = useState('');
  const [subTitle, setSubTitle] = useState('');
  const [title1, setTitle1] = useState('');
  const [title2, setTitle2] = useState('');
  const [title3, setTitle3] = useState('');
  const [title1Description, setTitle1Description] = useState('');
  const [title2Description, setTitle2Description] = useState('');
  const [title1Points, setTitle1Points] = useState([]);
  const [title3Points, setTitle3Points] = useState([]);
  const [services, setServices] = useState([]);

  const [newPoint1, setNewPoint1] = useState('');
  const [newPoint3, setNewPoint3] = useState('');
  const [newServiceTitle, setNewServiceTitle] = useState('');
  const [newServiceDesc, setNewServiceDesc] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const res = await axios.get('https://cpccbe.org/api/services');
    setServicesData(res.data);
  };

  const resetForm = () => {
    setTitle('');
    setSubTitle('');
    setTitle1('');
    setTitle2('');
    setTitle3('');
    setTitle1Description('');
    setTitle2Description('');
    setTitle1Points([]);
    setTitle3Points([]);
    setServices([]);
    setNewPoint1('');
    setNewPoint3('');
    setNewServiceTitle('');
    setNewServiceDesc('');
  };

  const handleOpenModal = (id = null) => {
    resetForm();
    setEditingId(id);
    setOpenModal(true);

    if (id) {
      const item = servicesData.find((i) => i._id === id);
      if (item) {
        setTitle(item.title);
        setSubTitle(item.subTitle);
        setTitle1(item.title1);
        setTitle2(item.title2);
        setTitle3(item.title3);
        setTitle1Description(item.title1Description);
        setTitle2Description(item.title2Description);
        setTitle1Points(item.title1Points || []);
        setTitle3Points(item.title3Points || []);
        setServices(item.services || []);
      }
    }
  };

  const handleSave = async () => {
    const payload = {
      title,
      subTitle,
      title1,
      title2,
      title3,
      title1Description,
      title2Description,
      title1Points,
      title3Points,
      services,
    };

    if (editingId) {
      await axios.put(`https://cpccbe.org/api/services/${editingId}`, payload);
    } else {
      await axios.post('https://cpccbe.org/api/services', payload);
    }

    fetchData();
    setOpenModal(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this item?')) {
      await axios.delete(`https://cpccbe.org/api/services/${id}`);
      fetchData();
    }
  };

  // array deletes (EDIT ONLY)
  const deletePoint1 = (i) => setTitle1Points(title1Points.filter((_, idx) => idx !== i));
  const deletePoint3 = (i) => setTitle3Points(title3Points.filter((_, idx) => idx !== i));
  const deleteService = (i) => setServices(services.filter((_, idx) => idx !== i));

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = servicesData.map((row) => row._id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleRowClick = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <Page title="What We Do">
      <Container>
        <Stack direction="row" justifyContent="space-between" mb={4}>
          <Typography variant="h4">What We Do</Typography>
          <Button variant="contained" onClick={() => handleOpenModal()}>
            New
          </Button>
        </Stack>

        <Card>
          <Scrollbar>
            <TableContainer>
              <Table>
                <HrconclaveListHead
                  headLabel={TABLE_HEAD}
                  rowCount={servicesData.length}
                  numSelected={selected.length}
                  order={order}
                  orderBy={orderBy}
                  onSelectAllClick={handleSelectAllClick}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {servicesData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const isItemSelected = selected.indexOf(row._id) !== -1;
                    return (
                      <TableRow key={row._id} selected={isItemSelected}>
                        <TableCell padding="checkbox">
                          <Checkbox checked={isItemSelected} onChange={() => handleRowClick(row._id)} />
                        </TableCell>
                        <TableCell align="left">{row.title}</TableCell>
                        <TableCell align="left">{row.subTitle}</TableCell>

                        <TableCell align="left">
                          <Typography fontWeight="bold">{row.title1}</Typography>
                          {row.title1Points?.map((p, i) => (
                            <Typography key={i} variant="caption">
                              • {p}
                            </Typography>
                          ))}
                        </TableCell>

                        <TableCell align="left">
                          <Typography fontWeight="bold">{row.title2}</Typography>
                          <Typography variant="caption">{row.title2Description}</Typography>
                        </TableCell>

                        <TableCell align="left">
                          <Typography fontWeight="bold">{row.title3}</Typography>
                          {row.title3Points?.map((p, i) => (
                            <Typography key={i} variant="caption">
                              • {p}
                            </Typography>
                          ))}
                        </TableCell>

                        <TableCell align="left">
                          {row.services?.map((s, i) => (
                            <Typography key={i} variant="caption">
                              • {s.title}
                            </Typography>
                          ))}
                        </TableCell>

                        <TableCell align="right">
                          <HrconclaveMoreMenu
                            onEdit={() => handleOpenModal(row._id)}
                            onDelete={() => handleDelete(row._id)}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            component="div"
            count={servicesData.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(e, p) => setPage(p)}
            onRowsPerPageChange={(e) => setRowsPerPage(+e.target.value)}
          />
        </Card>

        {/* EDIT MODAL */}
        <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="md" fullWidth>
          <DialogTitle>{editingId ? 'Edit' : 'Create'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField label="Main Title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth />
              <TextField label="Sub Title" value={subTitle} onChange={(e) => setSubTitle(e.target.value)} fullWidth />

              {/* SECTION 1: Consulting Process */}
              <Box sx={{ border: '1px solid #ddd', p: 2, borderRadius: 1 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#002b5e' }}>
                  Section 1: Consulting Process
                </Typography>
                <TextField
                  label="Title"
                  value={title1}
                  onChange={(e) => setTitle1(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Description"
                  value={title1Description}
                  onChange={(e) => setTitle1Description(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  sx={{ mb: 2 }}
                />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Points:
                </Typography>
                <Stack direction="row" sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    placeholder="Add new point"
                    value={newPoint1}
                    onChange={(e) => setNewPoint1(e.target.value)}
                  />
                  <Button
                    onClick={() => {
                      if (newPoint1) setTitle1Points([...title1Points, newPoint1]);
                      setNewPoint1('');
                    }}
                  >
                    Add
                  </Button>
                </Stack>
                {title1Points.map((p, i) => (
                  <Stack key={i} direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography>• {p}</Typography>
                    <IconButton onClick={() => deletePoint1(i)} color="error" size="small">
                      <Iconify icon="eva:trash-2-outline" />
                    </IconButton>
                  </Stack>
                ))}
              </Box>

              {/* SECTION 2: Educational Offerings */}
              <Box sx={{ border: '1px solid #ddd', p: 2, borderRadius: 1 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#002b5e' }}>
                  Section 2: Educational Offerings
                </Typography>
                <TextField
                  label="Title"
                  value={title2}
                  onChange={(e) => setTitle2(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Description"
                  value={title2Description}
                  onChange={(e) => setTitle2Description(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Box>

              {/* SECTION 3: Additional Services */}
              <Box sx={{ border: '1px solid #ddd', p: 2, borderRadius: 1 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#002b5e' }}>
                  Section 3: Additional Services
                </Typography>
                <TextField
                  label="Title"
                  value={title3}
                  onChange={(e) => setTitle3(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Services List:
                </Typography>
                <Stack direction="row" sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    placeholder="Add new service"
                    value={newPoint3}
                    onChange={(e) => setNewPoint3(e.target.value)}
                  />
                  <Button
                    onClick={() => {
                      if (newPoint3) setTitle3Points([...title3Points, newPoint3]);
                      setNewPoint3('');
                    }}
                  >
                    Add
                  </Button>
                </Stack>
                {title3Points.map((p, i) => (
                  <Stack key={i} direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography>• {p}</Typography>
                    <IconButton onClick={() => deletePoint3(i)} color="error" size="small">
                      <Iconify icon="eva:trash-2-outline" />
                    </IconButton>
                  </Stack>
                ))}
              </Box>

              {/* Services Cards */}
              <Box sx={{ border: '1px solid #ddd', p: 2, borderRadius: 1 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#002b5e' }}>
                  Services Cards
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <TextField
                    label="Service Title"
                    value={newServiceTitle}
                    onChange={(e) => setNewServiceTitle(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Service Description"
                    value={newServiceDesc}
                    onChange={(e) => setNewServiceDesc(e.target.value)}
                    fullWidth
                  />
                  <Button
                    onClick={() => {
                      if (newServiceTitle && newServiceDesc) {
                        setServices([...services, { title: newServiceTitle, description: newServiceDesc }]);
                        setNewServiceTitle('');
                        setNewServiceDesc('');
                      }
                    }}
                  >
                    Add
                  </Button>
                </Stack>

                {services.map((s, i) => (
                  <Stack key={i} direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Box>
                      <Typography fontWeight="bold">{s.title}</Typography>
                      <Typography variant="caption">{s.description}</Typography>
                    </Box>
                    <IconButton onClick={() => deleteService(i)} color="error" size="small">
                      <Iconify icon="eva:trash-2-outline" />
                    </IconButton>
                  </Stack>
                ))}
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenModal(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSave}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
